'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FiCopy, FiCheck, FiRefreshCw, FiKey, FiInfo, FiLayers } from 'react-icons/fi'
import Swal from 'sweetalert2'

export default function TokenPage() {
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [copying, setCopying] = useState(false)
  const [usuario, setUsuario] = useState<any>(null)
  const router = useRouter()

  useEffect(() => {
    const cargarDatosIniciales = async () => {
      setLoading(true)
      try {
        const [usuarioRes, tokenRes] = await Promise.all([
          fetch('/api/auth/verificar', { cache: 'no-store' }),
          fetch('/api/user/token', { cache: 'no-store' })
        ])
        
        const usuarioData = await usuarioRes.json()
        const tokenData = await tokenRes.json()
        
        if (usuarioData.success) setUsuario(usuarioData.usuario)
        if (tokenData.success) setToken(tokenData.token)
      } catch (e) {
        console.error('Error cargando datos:', e)
      } finally {
        setLoading(false)
      }
    }
    
    cargarDatosIniciales()
  }, [])

  const fetchToken = async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/user/token', { cache: 'no-store' })
      const data = await res.json()
      if (data.success) {
        setToken(data.token)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateToken = async () => {
    const result = await Swal.fire({
      title: '¿Generar nuevo token?',
      text: "Esto reemplazará tu token actual y cualquier aplicación que lo use dejará de funcionar.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, generar',
      cancelButtonText: 'Cancelar'
    })

    if (result.isConfirmed) {
      setLoading(true)
      try {
        const res = await fetch('/api/user/token', { method: 'POST' })
        const data = await res.json()
        if (data.success) {
          setToken(data.token)
          Swal.fire('¡Éxito!', 'Nuevo token generado correctamente.', 'success')
          router.refresh()
        } else {
          Swal.fire('Atención', data.message || data.error || 'No se pudo generar el token.', 'warning')
        }
      } catch (e) {
        Swal.fire('Error', 'No se pudo contactar con el servidor.', 'error')
      } finally {
        setLoading(false)
      }
    }
  }

  const copyToClipboard = () => {
    if (!token) return
    navigator.clipboard.writeText(token)
    setCopying(true)
    setTimeout(() => setCopying(false), 2000)
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-gray-900">Configuración de API</h1>
        <p className="text-gray-500">Gestiona tu token de acceso para servicios externos.</p>
      </div>

      <Card className="border-none shadow-sm overflow-hidden border border-gray-100">
        <CardContent className="pt-10 pb-12 text-center px-6 lg:px-20">
          <div className="flex flex-col items-center gap-4">
            <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Hola {usuario?.nombreCompleto?.split(' ')[0] || 'Usuario'}, aquí está tu token único
            </h2>
            <p className="text-gray-600 max-w-xl text-center leading-relaxed">
              Este token es la autorización para todas nuestras APIS, que la vas a poder usar
              de manera rápida, segura y eficiente.
            </p>
            
            {token ? (
              <div className="w-full mt-8 space-y-4">
                <div className="flex items-center justify-center gap-2 text-green-600 font-bold text-sm">
                  <FiCheck className="animate-bounce" /> Token cargado correctamente
                </div>
                
                <div className="relative group">
                  <div className="bg-slate-50 border-2 border-dashed border-gray-200 rounded-2xl p-6 lg:p-8 font-mono text-xs lg:text-sm text-gray-600 break-all select-all min-h-[100px] flex items-center justify-center transition-all group-hover:bg-white group-hover:border-blue-400">
                    {loading ? (
                       <FiRefreshCw className="animate-spin h-6 w-6 text-blue-500" />
                    ) : token}
                  </div>
                  
                  <button 
                    onClick={copyToClipboard}
                    className="absolute -bottom-4 right-4 bg-white border shadow-lg rounded-full p-2.5 hover:scale-110 transition-transform active:scale-95 text-blue-600"
                    title="Copiar al portapapeles"
                  >
                    {copying ? <FiCheck className="h-5 w-5 text-green-500" /> : <FiCopy className="h-5 w-5" />}
                  </button>
                </div>
                </div>
            ) : (
              <div className="mt-10 p-10 bg-blue-50/50 rounded-3xl border border-blue-100 flex flex-col items-center gap-6">
                <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <FiKey className="h-8 w-8 text-blue-600" />
                </div>
                <div className="text-center">
                  <h3 className="font-bold text-blue-900">No tienes un token activo</h3>
                  <p className="text-sm text-blue-700 mt-1">Genera uno ahora para empezar a usar nuestras herramientas.</p>
                </div>
                <Button 
                  type="button"
                  onClick={handleGenerateToken}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 px-8 py-6 rounded-2xl font-bold shadow-xl shadow-blue-200"
                >
                  {loading ? 'Generando...' : 'Generar mi primer Token'}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-none shadow-sm bg-amber-50/50 border border-amber-100">
          <CardContent className="p-4 flex gap-3">
            <FiInfo className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 leading-relaxed">
              <span className="font-bold">Seguridad:</span> Nunca compartas este token con nadie. Permite el acceso completo a tus servicios de facturación y validación.
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-none shadow-sm bg-blue-50/50 border border-blue-100">
          <CardContent className="p-4 flex gap-3">
            <FiLayers className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-xs text-blue-800 leading-relaxed">
              <span className="font-bold">Documentación:</span> Puedes usar este token en la cabecera <code>Authorization: Bearer [TOKEN]</code> para todas tus peticiones REST.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
